import * as React from 'react'
import { PHONE_LANDSCAPE_QUERY } from 'utils/mobileLayout'

const PHONE_MAX_WIDTH = 767
const TABLET_MIN_SCREEN_EDGE = 700

const createMediaQueryHook = (
  query: string,
  matches: (mediaQuery: MediaQueryList) => boolean = (mediaQuery) =>
    mediaQuery.matches
) => {
  let mediaQuery: MediaQueryList | undefined
  const getMediaQuery = () => (mediaQuery ??= window.matchMedia(query))

  const subscribe = (onChange: () => void) => {
    getMediaQuery().addEventListener('change', onChange)
    return () => getMediaQuery().removeEventListener('change', onChange)
  }
  const getSnapshot = () => matches(getMediaQuery())
  const getServerSnapshot = () => false

  const useMediaQuery = () =>
    React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
  return useMediaQuery
}

export const useIsTouchDevice = createMediaQueryHook('(pointer: coarse)')

/**
 * Use the compact touch layout only on phone-sized touch viewports.
 * Do not use `useIsTouchDevice` for hover-vs-tap UI: Windows touchscreen
 * laptops match `(pointer: coarse)` even when the user is on a mouse.
 */
export const useIsTouchMobile = createMediaQueryHook(
  `(max-width: ${PHONE_MAX_WIDTH}px) and (pointer: coarse), ${PHONE_LANDSCAPE_QUERY} and (pointer: coarse)`,
  (mediaQuery) => {
    if (!mediaQuery.matches) return false

    // Viewport width alone classifies iPad Mini and iPad split view as phones.
    // The physical screen's short edge keeps tablets on the desktop UI while
    // still allowing phone orientation changes to retain the compact layout.
    const shortScreenEdge = Math.min(window.screen.width, window.screen.height)
    return shortScreenEdge < TABLET_MIN_SCREEN_EDGE
  }
)
